// frontend/src/pages/CheckoutPage/CheckoutPage.jsx
// Fase 4: Planos com layout vendável, foco no Completo, UI/UX e Marketing.

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Row, Col, Button, Spin, Alert, Typography, App, Tag, Modal, Form, Input, Radio, Space } from 'antd';
import { CrownOutlined, BankOutlined, RocketOutlined, CheckCircleOutlined, ExclamationCircleFilled, LoadingOutlined } from '@ant-design/icons';
import { useCepAutofill } from '../../hooks/useCepAutofill';
import { formatCepDisplay } from '../../utils/cepLookup';
import { useAuth } from '../../contexts/AuthContext';
import { getPlanos, criarCheckout } from '../../services/assinaturaService';
import { getErrorMessage } from '../../utils/errorHandler';
import { precisaAceiteLegal } from '../../utils/legalAccess';
import { isValidCpfOrCnpj } from '../../utils/cardValidation';
import { trackMetaEvent } from '../../utils/metaPixel';
import logo from '../../assets/images/logo_horizontal_fundo_transparente.png';
import './CheckoutPage.css';

const { Title, Text } = Typography;

const TIPO_CONFIG = {
  completo: {
    label: 'Completo',
    desc: 'Financeiro + Milhas em um só plano',
    icon: <CrownOutlined />,
    cor: '#B89040',
    destaque: true,
    beneficios: ['Gestão financeira', 'Controle de milhas', 'Tudo em um lugar'],
  },
  milhas: {
    label: 'Milhas',
    desc: 'Controle de milhas e pontos',
    icon: <RocketOutlined />,
    cor: '#1F4F4F',
    beneficios: ['Extrato de milhas', 'Programas e clubes'],
  },
  financeiro: {
    label: 'Financeiro',
    desc: 'Gestão financeira pessoal',
    icon: <BankOutlined />,
    cor: '#50B0A0',
    beneficios: ['Contas e cartões', 'Transações e metas'],
  },
};

const CICLO_CONFIG = {
  mensal: { label: 'Mensal', meses: 1, recorrencia: 'todo mês' },
  semestral: { label: 'Semestral', meses: 6, recorrencia: 'a cada 6 meses' },
  anual: { label: 'Anual', meses: 12, recorrencia: 'todo ano' },
};

// Resposta do primeiro acesso ("o que você quer organizar?") → plano sugerido.
const TIPO_POR_OBJETIVO = { financas: 'financeiro', milhas: 'milhas', ambos: 'completo' };

const formatarValor = (v) => Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PlanoCard = ({
  plano,
  cfg,
  cicloCfg,
  economia,
  valorMes,
  checkoutLoading,
  onAssinar,
  destaque,
}) => {
  const formatarValorFn = formatarValor;
  return (
    <Card
      hoverable
      className={`checkout-plano-card ${cfg.destaque ? 'checkout-plano-destaque' : ''} ${destaque ? 'checkout-plano-destaque-recontratar' : ''}`}
    >
      <div className="checkout-plano-inner">
        <div className="checkout-plano-badges">
          {destaque && (
            <Tag color="blue" className="checkout-tag">Seu plano anterior</Tag>
          )}
          {cfg.destaque && (plano.ciclo === 'semestral' || plano.ciclo === 'anual') && (
            <Tag color="gold" className="checkout-tag checkout-tag-recomendado">Recomendado</Tag>
          )}
          {plano.tipo === 'completo' && plano.ciclo === 'mensal' && (
            <Tag color="gold" className="checkout-tag">Mais popular</Tag>
          )}
          {plano.ciclo === 'anual' && economia && (
            <Tag color="green" className="checkout-tag">Economize {economia}%</Tag>
          )}
          {plano.ciclo === 'anual' && (
            <Tag color="blue" className="checkout-tag">Melhor custo-benefício</Tag>
          )}
        </div>
        <div className="checkout-plano-ciclo">{cicloCfg.label}</div>
        <div className="checkout-plano-valor">
          R$ {formatarValorFn(plano.valor)}
          {plano.ciclo !== 'mensal' && (
            <span className="checkout-plano-equiv">/ R$ {formatarValorFn(valorMes)}/mês</span>
          )}
        </div>
        <div className="checkout-plano-rec">Cobrança {cicloCfg.recorrencia}</div>
        <Button
          type="primary"
          size="large"
          block
          loading={checkoutLoading === plano.id}
          icon={<CheckCircleOutlined />}
          onClick={() => onAssinar(plano)}
          className="checkout-btn-assinar"
        >
          Assinar
        </Button>
      </div>
    </Card>
  );
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planoDestaqueId = searchParams.get('plano');
  const refPlanoDestaque = useRef(null);
  const { message, modal } = App.useApp();
  const { isAuthenticated, user } = useAuth();
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [modalDadosOpen, setModalDadosOpen] = useState(false);
  const [planoSelecionado, setPlanoSelecionado] = useState(null);
  const [formDados] = Form.useForm();
  const { cepLoading, cepInputProps } = useCepAutofill(formDados, message);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/cadastro', { replace: true }); return; }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const data = await getPlanos();
        setPlanos(data);
        setError(null);
      } catch (err) {
        setError(getErrorMessage(err, 'Não foi possível carregar os planos.'));
      } finally { setLoading(false); }
    };
    if (isAuthenticated) fetch();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!loading && planoDestaqueId && refPlanoDestaque.current) {
      refPlanoDestaque.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [loading, planoDestaqueId]);

  const tipoSugerido = TIPO_POR_OBJETIVO[user?.preferencias_ui?.objetivo] || null;

  const planosPorTipo = useMemo(() => {
    const padrao = ['completo', 'milhas', 'financeiro'];
    // O plano que combina com o objetivo da pessoa vem primeiro.
    const ordem = tipoSugerido
      ? [tipoSugerido, ...padrao.filter((t) => t !== tipoSugerido)]
      : padrao;
    const porTipo = {};
    planos.forEach((p) => {
      if (!porTipo[p.tipo]) porTipo[p.tipo] = [];
      porTipo[p.tipo].push(p);
    });
    const cicloOrd = { mensal: 0, semestral: 1, anual: 2 };
    return ordem.map((tipo) => ({
      tipo,
      planos: (porTipo[tipo] || []).sort((a, b) => (cicloOrd[a.ciclo] ?? 0) - (cicloOrd[b.ciclo] ?? 0)),
    }));
  }, [planos, tipoSugerido]);

  const valorMensalEquiv = (plano) => {
    const cfg = CICLO_CONFIG[plano.ciclo];
    return cfg.meses > 1 ? Number(plano.valor) / cfg.meses : Number(plano.valor);
  };

  const economiaVsMensal = (plano, planosDoTipo) => {
    const mensal = planosDoTipo.find((p) => p.ciclo === 'mensal');
    if (!mensal || plano.ciclo === 'mensal') return null;
    const valorMensal = Number(mensal.valor);
    const equiv = valorMensalEquiv(plano);
    const economia = Math.round(((valorMensal - equiv) / valorMensal) * 100);
    return economia > 0 ? economia : null;
  };

  const abrirModalDados = (plano) => {
    setPlanoSelecionado(plano);
    formDados.setFieldsValue({
      nome_completo: user?.nome_completo || '',
      cpf: user?.cpf || '',
      email: user?.email || '',
      telefone: user?.telefone || '',
      cep: formatCepDisplay(user?.cep || ''),
      endereco: user?.endereco || '',
      numero: user?.numero || '',
      complemento: user?.complemento || '',
      bairro: user?.bairro || '',
      cidade: user?.cidade || '',
      estado: user?.estado || '',
      forma_pagamento: 'PIX',
    });
    setModalDadosOpen(true);
  };

  const executarCheckout = async (plano, values) => {
    setCheckoutLoading(plano.id);
    try {
      const dadosCliente = {
        nome_completo: values.nome_completo,
        cpf: values.cpf,
        telefone: values.telefone || undefined,
        cep: values.cep || undefined,
        endereco: values.endereco || undefined,
        numero: values.numero || undefined,
        complemento: values.complemento || undefined,
        bairro: values.bairro || undefined,
        cidade: values.cidade || undefined,
        estado: values.estado || undefined,
      };
      const { checkout_url } = await criarCheckout(plano.id, dadosCliente, values.forma_pagamento || 'PIX');
      setModalDadosOpen(false);
      if (checkout_url) {
        try {
          const tipoCfg = TIPO_CONFIG[plano?.tipo] || {};
          sessionStorage.setItem('sa_pending_purchase', JSON.stringify({
            content_name: tipoCfg.label || plano?.tipo,
            value: plano?.valor != null ? Number(plano.valor) : undefined,
            currency: 'BRL',
          }));
        } catch (e) { /* sessionStorage indisponível — segue sem bloquear o checkout */ }
        window.location.href = checkout_url;
        return;
      }
      message.error('Não foi possível gerar o link de pagamento.');
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 403 && data?.codigo === 'aceite_legal_pendente') {
        sessionStorage.setItem('legal_redirect_after', '/checkout');
        navigate('/aceitar-termos');
        return;
      }
      message.error(getErrorMessage(err, 'Não foi possível iniciar o pagamento.'));
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleSubmitDadosCheckout = async () => {
    if (!planoSelecionado) return;
    let values;
    try {
      values = await formDados.validateFields();
    } catch (err) {
      if (err?.errorFields?.length) {
        message.warning('Corrija os campos destacados antes de continuar.');
      }
      return;
    }

    const plano = planoSelecionado;
    setModalDadosOpen(false);
    modal.confirm({
      title: 'Confirmar dados',
      icon: <ExclamationCircleFilled />,
      content: 'Tem certeza? Você não poderá alterar as informações de nome e CPF posteriormente.',
      okText: 'Sim, continuar',
      cancelText: 'Cancelar',
      centered: true,
      onCancel: () => setModalDadosOpen(true),
      onOk: () => executarCheckout(plano, values).catch((err) => {
        message.error(getErrorMessage(err, 'Erro ao iniciar checkout.'));
        setModalDadosOpen(true);
        return Promise.reject(err);
      }),
    });
  };

  const handleAssinar = (plano) => {
    const tipoCfg = TIPO_CONFIG[plano?.tipo] || {};
    trackMetaEvent('InitiateCheckout', {
      content_name: tipoCfg.label || plano?.tipo,
      currency: 'BRL',
      value: plano?.valor != null ? Number(plano.valor) : undefined,
    });
    if (user && precisaAceiteLegal(user)) {
      sessionStorage.setItem('legal_redirect_after', '/checkout');
      navigate('/aceitar-termos');
      return;
    }
    abrirModalDados(plano);
  };

  if (!isAuthenticated) return null;

  return (
    <div className="checkout-page">
      <div className="checkout-header">
        <img src={logo} alt="Logo Saldo Ativo" className="checkout-logo" />
        <Title level={2} className="checkout-title">Escolha seu plano</Title>
        <Text className="checkout-subtitle">Assine e tenha acesso completo ao Saldo Ativo</Text>
        <Text type="secondary" className="checkout-rec-info">Pagamento recorrente conforme o ciclo. Cancele quando quiser.</Text>
        {(() => {
          const fim = user?.data_fim_trial ? new Date(user.data_fim_trial) : null;
          const emTrial = fim && fim > new Date()
            && !user?.plano_financeiro_ativo && !user?.plano_milhas_ativo;
          if (!emTrial) return null;
          const dias = Math.max(1, Math.ceil((fim - new Date()) / 86400000));
          return (
            <Alert
              type="info"
              showIcon
              style={{ maxWidth: 520, margin: '12px auto 0' }}
              message={dias === 1 ? 'Último dia do seu teste grátis' : `Você ainda tem ${dias} dias de teste grátis`}
              description="Pode assinar agora sem pressa: a primeira cobrança fica agendada para o fim do seu teste. Você não perde os dias grátis que faltam e garante que o acesso não é interrompido."
            />
          );
        })()}
      </div>

      {loading ? (
        <div className="checkout-loading"><Spin size="large" tip="Carregando planos..." /></div>
      ) : error ? (
        <Alert type="error" message={error} showIcon style={{ maxWidth: 500, margin: '0 auto' }} />
      ) : (
        <div className="checkout-planos-wrapper">
          {planosPorTipo.map(({ tipo, planos: lista }) => {
            const cfg = TIPO_CONFIG[tipo] || {};
            return (
              <section
                key={tipo}
                className={`checkout-secao ${cfg.destaque ? 'checkout-secao-hero' : ''}`}
              >
                <div className="checkout-secao-header">
                  <span className="checkout-secao-icon" style={{ color: cfg.cor }}>{cfg.icon}</span>
                  <div>
                    <Title level={4} className="checkout-secao-titulo">
                      {cfg.label}
                      {tipo === tipoSugerido && (
                        <Tag color="gold" style={{ marginLeft: 8, verticalAlign: 'middle' }}>Sugerido para você</Tag>
                      )}
                    </Title>
                    <Text type="secondary" className="checkout-secao-desc">{cfg.desc}</Text>
                  </div>
                </div>
                <Row gutter={[24, 24]} className="checkout-planos-row">
                  {lista.map((plano) => (
                    <Col key={plano.id} xs={24} sm={12} md={8} ref={planoDestaqueId && String(plano.id) === String(planoDestaqueId) ? refPlanoDestaque : null}>
                      <PlanoCard
                        plano={plano}
                        cfg={cfg}
                        cicloCfg={CICLO_CONFIG[plano.ciclo] || {}}
                        economia={economiaVsMensal(plano, lista)}
                        valorMes={valorMensalEquiv(plano)}
                        checkoutLoading={checkoutLoading}
                        onAssinar={handleAssinar}
                        destaque={planoDestaqueId && String(plano.id) === String(planoDestaqueId)}
                      />
                    </Col>
                  ))}
                </Row>
              </section>
            );
          })}
        </div>
      )}

      <div className="checkout-footer">
        <Button type="link" onClick={() => navigate(-1)}>Voltar</Button>
      </div>

      <Modal
        title="Dados para cobrança"
        open={modalDadosOpen}
        onCancel={() => { setModalDadosOpen(false); setPlanoSelecionado(null); }}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Preencha seus dados para envio ao gateway de pagamento (Asaas).
        </Text>
        <Form form={formDados} layout="vertical" onFinish={handleSubmitDadosCheckout}>
          <Form.Item name="nome_completo" label="Nome completo" rules={[{ required: true, message: 'Obrigatório' }]}>
            <Input placeholder="Como no documento" disabled={!!(user?.cpf && String(user.cpf).trim())} />
          </Form.Item>
          <Form.Item
            name="cpf"
            label="CPF ou CNPJ"
            rules={[
              { required: true, message: 'Obrigatório para cobrança' },
              {
                validator: (_, value) => {
                  const digits = String(value || '').replace(/\D/g, '');
                  if (digits.length !== 11 && digits.length !== 14) return Promise.reject(new Error('CPF (11 dígitos) ou CNPJ (14 dígitos)'));
                  if (!isValidCpfOrCnpj(value)) return Promise.reject(new Error('CPF ou CNPJ inválido'));
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Apenas números" maxLength={18} disabled={!!(user?.cpf && String(user.cpf).trim())} />
          </Form.Item>
          <Form.Item name="email" label="E-mail">
            <Input disabled />
          </Form.Item>
          <Form.Item name="telefone" label="Telefone">
            <Input placeholder="(00) 00000-0000" maxLength={20} />
          </Form.Item>
          <Form.Item name="cep" label="CEP">
            <Input {...cepInputProps} suffix={cepLoading ? <LoadingOutlined spin /> : null} />
          </Form.Item>
          <Form.Item name="endereco" label="Endereço">
            <Input placeholder="Rua, avenida" />
          </Form.Item>
          <Form.Item name="numero" label="Número">
            <Input placeholder="Nº" maxLength={20} />
          </Form.Item>
          <Form.Item name="complemento" label="Complemento">
            <Input placeholder="Apto, bloco" maxLength={100} />
          </Form.Item>
          <Form.Item name="bairro" label="Bairro">
            <Input maxLength={100} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={16}>
              <Form.Item name="cidade" label="Cidade">
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="estado" label="UF">
                <Input placeholder="SP" maxLength={2} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="forma_pagamento" label="Forma de pagamento" rules={[{ required: true, message: 'Escolha a forma de pagamento' }]}>
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="PIX">PIX</Radio>
                <Radio value="CREDIT_CARD">Cartão de crédito (cobrança automática todo ciclo)</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" block size="large" loading={checkoutLoading !== null}>
              Continuar para pagamento
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CheckoutPage;
